// Anfragen-Zentrale (docs/anfragen-zentrale/SPEZIFIKATION.md §5–§8): eingehende E-Mails als Anliegen „E-Mail“ im Verkauf –
// Einordnung Bestellung · Veranstaltung · Filiale · Sonstiges (Regeln, optional Vorschlag der KI, jederzeit von Hand), erkannte
// Angaben (Kunde, Filiale, Positionen, Termine, Veranstaltung) und das Weiterverarbeiten: Auftrag anlegen und bestätigen,
// Umlagerung an die Filiale anfordern, Daten für sales.event.create bereithalten.
//
// Inhalte von E-Mails sind fremder Text: Die Oberfläche zeigt sie nur als Text (nie HTML), und dem KI-Assistenten bleiben alle
// Verträge vorenthalten (assistant: 'never') – die KI sieht E-Mails nur auf dem eigenen, abgeschotteten Weg der Einordnung (§6.3).
// Der Funktionsschalter „Anfragen per E-Mail“ (mailrequests) ist kein Branchenschalter: Liste und Einstellungen prüfen ihn in der
// Aktion (wie die E-Rechnung) – vorhandene E-Mail-Anliegen bleiben auch ausgeschaltet erreichbar und lassen sich abschließen.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { MAIL_CATEGORIES, MAIL_CATEGORY_SOURCES, SENDER_AUTH_STATES } from '../lib/mailRequests'
import { zDay } from './common'

const READ = 'sales.case.read'
const WRITE = 'sales.case.write'
const MANAGE = 'sales.mailRequest.manage'

const zCategory = z.enum(MAIL_CATEGORIES)
const zLocationBrief = z.object({ id: zId, code: z.string(), name: z.string() })
const zCustomerBrief = z.object({ id: zId, number: z.string().nullable(), name: z.string() })

// ---- Erkanntes (Form in sales_mail_request.extraction) -------------------------------------------------------------

export const zMailLine = z.object({
  /** Nummer der Angabe in der E-Mail (stabil für die Oberfläche) */
  index: z.int(),
  /** Zeile, aus der die Angabe stammt */
  text: z.string(),
  /** Menge in Tausendstel des Gebindes; null = keine Menge genannt */
  qty: z.int().nullable(),
  unitWord: z.string().nullable(),
  itemText: z.string(),
  code: z.string().nullable(),
  status: z.enum(['unique', 'ambiguous', 'none']),
  itemId: zId.nullable(),
  itemName: z.string().nullable(),
  sku: z.string().nullable(),
  /** Leihartikel (Zapfanlage, Garnitur …) – bei einer Veranstaltung Leihwunsch statt Getränk */
  rental: z.boolean(),
  unitId: zId.nullable(),
  unitLabel: z.string().nullable(),
  /** Basismenge des Gebindes in Tausendstel (Kasten 20 × 0,5 l = 20000) – für Mengen in der Basiseinheit */
  unitBaseQty: z.int().nullable().default(null),
  candidates: z.array(z.object({ itemId: zId, sku: z.string(), name: z.string(), score: z.int() })),
})
export type MailLine = z.output<typeof zMailLine>

export const zMailCustomerMatch = z.object({
  status: z.enum(['unique', 'ambiguous', 'none']),
  customerId: zId.nullable(),
  candidates: z.array(
    z.object({
      id: zId,
      number: z.string().nullable(),
      name: z.string(),
      /**
       * email: E-Mail des Kunden · contact: E-Mail eines Ansprechpartners · domain: gleiche Domäne · number: Kundennummer im Text ·
       * forwarded: Absender einer weitergeleiteten Nachricht im Text
       */
      matchedBy: z.enum(['email', 'contact', 'domain', 'number', 'forwarded']),
    }),
  ),
})

export const zMailVenue = z.object({ street: z.string().nullable(), postalCode: z.string().nullable(), city: z.string().nullable(), note: z.string().nullable() })

export const zMailExtraction = z.object({
  customer: zMailCustomerMatch,
  /** Absender ist eine Filiale (hinterlegte Adresse) */
  location: zLocationBrief.nullable(),
  lines: z.array(zMailLine),
  /** Wunschtermin (Lieferung) */
  desiredDate: z.string().nullable(),
  dates: z.array(z.object({ date: z.string(), until: z.string().nullable(), kind: z.enum(['delivery', 'event', 'unknown']), text: z.string() })),
  customerNumber: z.string().nullable(),
  event: z.object({
    title: z.string(),
    date: z.string().nullable(),
    endDate: z.string().nullable(),
    guests: z.int().nullable(),
    venue: zMailVenue.nullable(),
    phone: z.string().nullable(),
  }),
})
export type MailExtraction = z.output<typeof zMailExtraction>

// ---- Anzeige ---------------------------------------------------------------------------------------------------------

const zAttachment = z.object({
  name: z.string(),
  contentType: z.string(),
  size: z.int(),
  /** Adresse zum Laden; null = nicht übernommen (note) */
  url: z.string().nullable(),
  inline: z.boolean(),
  note: z.string().nullable(),
})

const zClassification = z.object({
  category: zCategory,
  categoryLabel: z.string(),
  source: z.enum(MAIL_CATEGORY_SOURCES),
  sourceLabel: z.string(),
  /** 0…100 */
  confidence: z.int(),
  reason: z.string().nullable(),
  rules: z.object({ category: zCategory, confidence: z.int(), reason: z.string() }).nullable(),
  ai: z
    .object({
      status: z.enum(['pending', 'done', 'failed', 'skipped']),
      category: zCategory.nullable(),
      confidence: z.int().nullable(),
      reason: z.string().nullable(),
      /** Satz zum Stand (nicht verfügbar, Budget erschöpft …) */
      message: z.string().nullable(),
      at: z.date().nullable(),
      /** Der Text sah nach einer Anweisung an eine KI aus – Hinweis zum sorgfältigen Prüfen */
      flagged: z.boolean().default(false),
    })
    .nullable(),
})

const zDocumentBrief = z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string() })

export const zMailRequestDetail = z.object({
  caseId: zId,
  status: z.enum(['open', 'accepted', 'rejected', 'done', 'failed']),
  statusLabel: z.string(),
  open: z.boolean(),
  message: z.object({
    id: zId,
    source: z.enum(['imap', 'upload']),
    fromAddress: z.string().nullable(),
    fromName: z.string().nullable(),
    fromText: z.string(),
    to: z.array(z.string()),
    subject: z.string(),
    /** reiner Text – nie HTML */
    text: z.string(),
    textSource: z.enum(['plain', 'html', 'none']),
    receivedAt: z.date(),
    sentAt: z.date().nullable(),
    attachments: z.array(zAttachment),
    notes: z.array(z.string()),
    /** Rohfassung (.eml) zum Laden */
    rawUrl: z.string().nullable(),
    senderAuth: z.enum(SENDER_AUTH_STATES),
    senderAuthLabel: z.string(),
  }),
  classification: zClassification,
  extraction: zMailExtraction,
  customer: zCustomerBrief.nullable(),
  location: zLocationBrief.nullable(),
  results: z.object({
    order: zDocumentBrief.nullable(),
    transfer: zDocumentBrief.nullable(),
    /** angelegte Veranstaltung (sales.mailRequest.createEvent) */
    event: zDocumentBrief.nullable(),
    /** Eingabe für sales.event.create – null, solange es keine Veranstaltung ist */
    eventInput: z.record(z.string(), z.unknown()).nullable(),
    auto: z.object({ result: z.string(), reason: z.string().nullable(), at: z.date().nullable() }).nullable(),
  }),
  settings: z.object({
    aiEnabled: z.boolean(),
    autoOrder: z.boolean(),
    orderLocation: zLocationBrief.nullable(),
    transferFromLocation: zLocationBrief.nullable(),
  }),
})
export type MailRequestDetail = z.output<typeof zMailRequestDetail>

export const zMailRequestRow = z.object({
  caseId: zId,
  status: z.enum(['open', 'accepted', 'rejected', 'done', 'failed']),
  statusLabel: z.string(),
  open: z.boolean(),
  category: zCategory,
  categoryLabel: z.string(),
  categorySource: z.enum(MAIL_CATEGORY_SOURCES),
  confidence: z.int(),
  subject: z.string(),
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
  receivedAt: z.date(),
  customer: zCustomerBrief.nullable(),
  location: zLocationBrief.nullable(),
  lineCount: z.int(),
  /** Positionen, die noch nicht eindeutig sind */
  unresolved: z.int(),
  desiredDate: z.string().nullable(),
  orderNumber: z.string().nullable(),
  transferNumber: z.string().nullable(),
  autoResult: z.string().nullable(),
})

// ---- Lesen -------------------------------------------------------------------------------------------------------------

export const mailRequestList = defineContract({
  name: 'sales.mailRequest.list',
  title: 'Anfragen per E-Mail anzeigen',
  description: 'Eingegangene E-Mails als Anliegen: Einordnung, Absender, erkannter Kunde, Positionen, Zustand – offene zuerst.',
  llm: { description: 'Liste der Anfragen per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    view: z.enum(['open', 'closed', 'all']).default('open'),
    category: zCategory.optional(),
    q: z.string().trim().max(100).optional(),
  }),
  output: z.object({
    rows: z.array(zMailRequestRow),
    counts: z.object({ open: z.int(), closed: z.int(), order: z.int(), event: z.int(), branch: z.int(), other: z.int() }),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

export const mailRequestCount = defineContract({
  name: 'sales.mailRequest.count',
  title: 'Offene Anfragen per E-Mail zählen',
  description: 'Anzahl der E-Mail-Anliegen, die noch zu bearbeiten sind.',
  llm: { description: 'Zählt offene Anfragen per E-Mail.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

export const mailRequestGet = defineContract({
  name: 'sales.mailRequest.get',
  title: 'Anfrage per E-Mail anzeigen',
  description: 'Eine E-Mail als Anliegen: Absender, Betreff, Text (nur als Text), Anhänge, Einordnung mit Begründung, erkannte Angaben, Folgebelege.',
  llm: { description: 'Einzelne Anfrage per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId }),
  output: zMailRequestDetail,
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

// ---- Einordnen, zuordnen, abschließen ----------------------------------------------------------------------------------

export const mailRequestClassify = defineContract({
  name: 'sales.mailRequest.classify',
  title: 'Anfrage per E-Mail einordnen',
  description: 'Ordnet eine E-Mail von Hand ein: Bestellung, Veranstaltung, Filiale oder Sonstiges.',
  llm: { description: 'Einordnung einer Anfrage per E-Mail ändern. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId, category: zCategory }),
  output: zMailRequestDetail,
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
})

export const mailRequestReclassify = defineContract({
  name: 'sales.mailRequest.reclassify',
  title: 'Anfrage per E-Mail neu erkennen',
  description: 'Liest die E-Mail noch einmal: Kunde, Filiale, Positionen, Termine und die Einordnung nach den Regeln – bei eingeschalteter KI auch ihr Vorschlag. Eine Einordnung von Hand bleibt.',
  llm: { description: 'Anfrage per E-Mail neu erkennen. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId }),
  output: zMailRequestDetail,
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
})

export const mailRequestSetCustomer = defineContract({
  name: 'sales.mailRequest.setCustomer',
  title: 'Kunden einer Anfrage per E-Mail wählen',
  description: 'Legt den Kunden fest, wenn der Absender keinem oder mehreren Kunden zugeordnet werden konnte.',
  llm: { description: 'Kunde einer Anfrage per E-Mail wählen. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId, customerId: zId.nullable() }),
  output: zMailRequestDetail,
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
})

export const mailRequestClose = defineContract({
  name: 'sales.mailRequest.close',
  title: 'Anfrage per E-Mail abschließen',
  description: 'Schließt eine E-Mail ab – erledigt (z. B. beantwortet) oder verworfen (Werbung, falsch zugestellt). Notiz bzw. Grund bleiben am Anliegen.',
  llm: { description: 'Anfrage per E-Mail abschließen. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId, outcome: z.enum(['done', 'discard']), note: z.string().trim().max(300).optional() }),
  output: zMailRequestDetail,
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
  auditInput: 'omit',
})

// ---- Weiterverarbeiten (§7) ----------------------------------------------------------------------------------------------

const zOrderLine = z.strictObject({
  itemId: zId,
  /** Gebinde (item_unit.id); ohne Angabe das Verkaufs-Standardgebinde */
  unitId: zId.nullish(),
  /** Menge im Gebinde in Tausendstel (1 Kasten = 1000) */
  qty: z.int().min(1, { error: 'Bitte geben Sie eine Menge größer als 0 an.' }).max(100_000_000),
})

export const mailRequestCreateOrder = defineContract({
  name: 'sales.mailRequest.createOrder',
  title: 'Auftrag aus einer E-Mail anlegen',
  description:
    'Legt aus einer Bestellung per E-Mail einen Auftrag an (Herkunft E-Mail, Kunde, Positionen, Liefertermin) und bestätigt ihn – mit allen Sperren (Rabattfreigabe, Kreditlimit). Bestätigt steht er in der Kommissionierliste.',
  llm: { description: 'Auftrag aus einer Anfrage per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    caseId: zId,
    customerId: zId,
    /** liefernder Standort; ohne Angabe der aus den Einstellungen */
    locationId: zId.nullish(),
    deliveryDate: zDay.nullish(),
    lines: z.array(zOrderLine).min(1, { error: 'Bitte übernehmen Sie mindestens eine Position.' }).max(200),
    notes: z.string().trim().max(2000).nullish(),
    /** gleich bestätigen (Standard) – sonst bleibt der Auftrag „Eingegangen“ */
    confirm: z.boolean().default(true),
  }),
  output: z.object({ detail: zMailRequestDetail, orderId: zId, message: z.string() }),
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
  limits: { maxItems: 200, itemCount: (input) => input.lines.length },
})

export const mailRequestRequestTransfer = defineContract({
  name: 'sales.mailRequest.requestTransfer',
  title: 'Umlagerung für eine Filiale aus einer E-Mail anfordern',
  description: 'Fordert für die Anfrage einer Filiale eine Umlagerung vom Zentrallager an – mit den erkannten Positionen.',
  llm: { description: 'Umlagerung aus einer Anfrage per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    caseId: zId,
    fromLocationId: zId,
    toLocationId: zId,
    deliveryDate: zDay.nullish(),
    lines: z.array(zOrderLine).min(1, { error: 'Bitte übernehmen Sie mindestens eine Position.' }).max(200),
    notes: z.string().trim().max(2000).nullish(),
  }),
  output: z.object({ detail: zMailRequestDetail, transferId: zId, message: z.string() }),
  permission: WRITE,
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
  limits: { maxItems: 200, itemCount: (input) => input.lines.length },
})

export const mailRequestCreateEvent = defineContract({
  name: 'sales.mailRequest.createEvent',
  title: 'Veranstaltung aus einer E-Mail anlegen',
  description:
    'Legt aus einer Veranstaltungsanfrage per E-Mail eine Veranstaltung im Zustand „Anfrage“ an – mit dem Erkannten (Anlass, Termine, Ort, Gäste, Getränke, Leihartikel) und dem Kunden bzw. Absender als Interessent. Angebot und Zusage folgen im Reiter „Veranstaltungen“.',
  llm: { description: 'Veranstaltung aus einer Anfrage per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ caseId: zId }),
  output: z.object({ detail: zMailRequestDetail, eventId: zId, number: z.string(), message: z.string() }),
  permission: WRITE,
  // das Anlegen selbst prüft sales.event.create (Recht am Standort) über ctx.call
  feature: 'events',
  risk: 'write',
  scope: 'tenant',
  assistant: 'never',
  effects: { reversible: false },
})

// ---- Einstellungen ------------------------------------------------------------------------------------------------------

export const zMailRequestSettings = z.object({
  aiEnabled: z.boolean(),
  /** Der KI-Assistent ist für den Betrieb eingeschaltet (Voraussetzung für die KI-Einordnung) */
  assistantEnabled: z.boolean(),
  autoOrder: z.boolean(),
  requireSenderAuth: z.boolean(),
  authservId: z.string().nullable(),
  orderLocationId: zId.nullable(),
  transferFromLocationId: zId.nullable(),
  addresses: z.array(z.object({ locationId: zId, address: z.string() })),
  locations: z.array(zLocationBrief.extend({ kind: z.string() })),
  /** Postfach im Kern eingerichtet und aktiv (Einstellungen › E-Mail-Eingang) */
  inboxActive: z.boolean(),
})

export const mailRequestSettings = defineContract({
  name: 'sales.mailRequest.settings',
  title: 'Einstellungen der Anfragen per E-Mail anzeigen',
  description: 'KI zur Einordnung, automatische Übernahme, Absenderprüfung, Standorte und die Adressen der Filialen.',
  llm: { description: 'Einstellungen der Anfragen per E-Mail. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: zMailRequestSettings,
  permission: MANAGE,
  risk: 'read',
  assistant: 'never',
})

export const mailRequestSaveSettings = defineContract({
  name: 'sales.mailRequest.saveSettings',
  title: 'Einstellungen der Anfragen per E-Mail ändern',
  description: 'Schaltet KI-Einordnung und automatische Übernahme ein oder aus, legt Absenderprüfung, Standorte und Filial-Adressen fest.',
  llm: { description: 'Einstellungen der Anfragen per E-Mail ändern. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    aiEnabled: z.boolean(),
    autoOrder: z.boolean(),
    requireSenderAuth: z.boolean(),
    authservId: z.string().trim().max(253).nullish(),
    orderLocationId: zId.nullish(),
    transferFromLocationId: zId.nullish(),
    addresses: z.array(z.strictObject({ locationId: zId, address: z.string().trim().min(3).max(254) })).max(100),
  }),
  output: zMailRequestSettings,
  permission: MANAGE,
  risk: 'write',
  assistant: 'never',
})
